import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { CheckCircle2, AlertTriangle, Info, XCircle } from 'lucide-react';

/**
 * ExtractionPreview - Shows OCR-extracted shot data for user verification/editing
 * Allows users to correct any misread values before proceeding with analysis
 *
 * Now includes validation indicators:
 * - GREEN (✓): Measured value within bounds
 * - YELLOW (ⓘ): Calculated from other metrics
 * - RED (⚠): Outside bounds or suspicious
 * - GRAY (--): Missing data
 *
 * Build: 2026-02-01-v2
 */
export const ExtractionPreview = ({ extractedData, onConfirm, onCancel, onEdit }) => {
  const [editedShots, setEditedShots] = useState(extractedData.shots || []);
  const [includedShots, setIncludedShots] = useState(
    (extractedData.shots || []).map(() => true)
  );
  const [isEditing, setIsEditing] = useState(false);

  // Sync state when extractedData changes
  useEffect(() => {
    if (extractedData && extractedData.shots) {
      setEditedShots(extractedData.shots);
      setIncludedShots(extractedData.shots.map(() => true));

      // Debug: Log validation data
      console.log('ExtractionPreview received data:', {
        shotsCount: extractedData.shots.length,
        hasValidation: !!extractedData.validation,
        validationCount: extractedData.validation?.length,
        firstValidation: extractedData.validation?.[0]
      });
    }
  }, [extractedData]);

  if (!extractedData || !extractedData.shots) {
    return null;
  }

  // Validation ranges for golf metrics
  const validationRanges = {
    ball_speed: { min: 50, max: 250, unit: 'mph' },
    club_speed: { min: 40, max: 200, unit: 'mph' },
    carry_distance: { min: 0, max: 450, unit: 'yards' },
    total_distance: { min: 0, max: 500, unit: 'yards' },
    offline_distance: { min: -100, max: 100, unit: 'yards' },
    launch_angle: { min: -10, max: 40, unit: 'degrees' },
    spin_rate: { min: 0, max: 10000, unit: 'rpm' },
    spin_axis: { min: -45, max: 45, unit: 'degrees' },
    side_spin: { min: -3000, max: 3000, unit: 'rpm' },
    attack_angle: { min: -15, max: 15, unit: 'degrees' },
    club_path: { min: -20, max: 20, unit: 'degrees' },
    face_angle: { min: -20, max: 20, unit: 'degrees' },
    dynamic_loft: { min: 0, max: 50, unit: 'degrees' },
    smash_factor: { min: 1.0, max: 1.6, unit: '' },
    max_height: { min: 0, max: 150, unit: 'feet' },
  };

  const handleFieldEdit = (shotIndex, field, value) => {
    const updated = [...editedShots];

    // Parse numeric value or set to null
    const numValue = value === '' ? null : parseFloat(value);

    // Validate against ranges if value provided
    let finalValue = isNaN(numValue) ? null : numValue;
    if (finalValue !== null && validationRanges[field]) {
      const { min, max } = validationRanges[field];
      finalValue = Math.max(min, Math.min(max, finalValue));
    }

    updated[shotIndex] = {
      ...updated[shotIndex],
      [field]: finalValue
    };
    setEditedShots(updated);
  };

  const handleConfirm = () => {
    // Filter to only included shots
    const finalShots = editedShots.filter((_, idx) => includedShots[idx]);

    // Pass edited data back to parent
    onConfirm({
      ...extractedData,
      shots: finalShots
    });
  };

  const toggleShotInclusion = (idx) => {
    const updated = [...includedShots];
    updated[idx] = !updated[idx];
    setIncludedShots(updated);
  };

  // Get validation flag for a specific shot and metric
  const getMetricFlag = (shotIndex, metricKey) => {
    const validation = extractedData.validation?.[shotIndex];
    if (!validation) return null;

    const flag = validation.flags?.find(f => f.metric === metricKey);
    return flag || null;
  };

  // Get status indicator for a metric value
  const getStatusIndicator = (flag, hasValue) => {
    if (!flag) {
      // No validation data - show default
      return hasValue ? { color: 'text-emerald-400', icon: null, tooltip: 'Measured value' } : { color: 'text-zinc-500', icon: null, tooltip: 'No data' };
    }

    switch (flag.status) {
      case 'valid':
        return { color: 'text-emerald-400', icon: CheckCircle2, tooltip: 'Measured value within normal range' };
      case 'inferred':
        return { color: 'text-yellow-400', icon: Info, tooltip: flag.reason || 'Calculated from other metrics' };
      case 'suspicious':
        return { color: 'text-red-400', icon: AlertTriangle, tooltip: flag.reason || 'Value outside expected range' };
      case 'missing':
        return { color: 'text-zinc-500', icon: XCircle, tooltip: 'No data available' };
      default:
        return { color: 'text-zinc-400', icon: null, tooltip: '' };
    }
  };

  // Define fields to display (in order of importance)
  const fields = [
    { key: 'ball_speed', label: 'Ball Speed', unit: 'mph' },
    { key: 'club_speed', label: 'Club Speed', unit: 'mph' },
    { key: 'smash_factor', label: 'Smash', unit: '' },
    { key: 'carry_distance', label: 'Carry', unit: 'yds' },
    { key: 'total_distance', label: 'Total', unit: 'yds' },
    { key: 'offline_distance', label: 'Offline', unit: 'yds', highlight: true },
    { key: 'launch_angle', label: 'Launch', unit: '°' },
    { key: 'spin_rate', label: 'Spin', unit: 'rpm' },
    { key: 'spin_axis', label: 'Spin Axis', unit: '°' },
    { key: 'side_spin', label: 'Side Spin', unit: 'rpm' },
    { key: 'attack_angle', label: 'Attack', unit: '°' },
    { key: 'club_path', label: 'Path', unit: '°' },
    { key: 'face_angle', label: 'Face', unit: '°' },
    { key: 'dynamic_loft', label: 'Dyn Loft', unit: '°' },
    { key: 'max_height', label: 'Height', unit: 'ft' },
  ];

  return (
    <Card className="bg-surface border border-accent-primary/30">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              📊 Extraction Preview
            </h3>
            <p className="text-sm text-muted mt-1">
              Review the extracted data. Click Edit to correct any misread values.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={isEditing ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? '✓ Done Editing' : '✏️ Edit Values'}
            </Button>
          </div>
        </div>

        {/* Shot count summary */}
        <div className="mb-4 p-3 bg-accent-primary/10 border border-accent-primary/30 rounded-lg">
          <p className="text-sm text-foreground">
            <span className="font-semibold">{editedShots.length} shot{editedShots.length !== 1 ? 's' : ''}</span> extracted
            <span className="ml-2 text-muted">
              ({includedShots.filter(Boolean).length} included)
            </span>
            {extractedData.data && (
              <span className="ml-2 text-muted">
                • Avg Carry: <span className="text-foreground font-medium">{extractedData.data.carry_distance?.toFixed(1) || 'N/A'} yds</span>
                {extractedData.data.offline_distance != null && (
                  <span className="ml-2">
                    • Avg Offline: <span className="text-foreground font-medium">
                      {extractedData.data.offline_distance > 0 ? '+' : ''}{extractedData.data.offline_distance?.toFixed(1)} yds
                    </span>
                  </span>
                )}
              </span>
            )}
          </p>
        </div>

        {/* Validation Legend */}
        {extractedData.validation && extractedData.validation.length > 0 ? (
          <div className="mb-4 p-3 bg-zinc-900/50 border border-zinc-700 rounded-lg">
            <p className="text-xs font-semibold text-zinc-400 mb-2">METRIC STATUS LEGEND:</p>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-zinc-300">Measured</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 text-yellow-400" />
                <span className="text-zinc-300">Calculated</span>
              </div>
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-zinc-300">Suspicious</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-500">—</span>
                <span className="text-zinc-300">Missing</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              ℹ️ Validation data not available. Metrics will be displayed without status indicators.
            </p>
          </div>
        )}

        {/* Data table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-2 text-muted font-medium w-8 sticky left-0 bg-surface z-10">
                  <input
                    type="checkbox"
                    checked={includedShots.every(Boolean)}
                    onChange={(e) => setIncludedShots(includedShots.map(() => e.target.checked))}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-accent-primary focus:ring-2 focus:ring-accent-primary"
                    title="Include/exclude all shots"
                  />
                </th>
                <th className="text-left py-2 px-2 text-muted font-medium sticky left-[48px] bg-surface z-10">#</th>
                {fields.map(field => (
                  <th
                    key={field.key}
                    className={`text-right py-2 px-2 font-medium ${
                      field.highlight ? 'text-accent-primary' : 'text-muted'
                    }`}
                  >
                    {field.label}
                    <span className="text-xs ml-1 opacity-60">({field.unit})</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {editedShots.map((shot, idx) => {
                const isIncluded = includedShots[idx];
                const validation = extractedData.validation?.[idx];
                const hasSuspicious = validation?.has_issues;

                return (
                  <tr
                    key={idx}
                    className={`border-b border-border/50 hover:bg-surface-hover ${
                      !isIncluded ? 'opacity-40' : ''
                    } ${hasSuspicious ? 'bg-red-500/5' : ''}`}
                  >
                    <td className="py-2 px-2 sticky left-0 bg-surface z-10">
                      <input
                        type="checkbox"
                        checked={isIncluded}
                        onChange={() => toggleShotInclusion(idx)}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-accent-primary focus:ring-2 focus:ring-accent-primary"
                        title={isIncluded ? "Exclude this shot" : "Include this shot"}
                      />
                    </td>
                    <td className="py-2 px-2 text-muted font-medium sticky left-[48px] bg-surface z-10">
                      {shot.shot_number || idx + 1}
                    </td>
                    {fields.map(field => {
                      const value = shot[field.key];
                      const hasValue = value != null;
                      const flag = getMetricFlag(idx, field.key);
                      const indicator = getStatusIndicator(flag, hasValue);
                      const StatusIcon = indicator.icon;

                      return (
                        <td
                          key={field.key}
                          className={`text-right py-2 px-2 relative group ${
                            field.highlight && hasValue ? 'text-accent-primary font-medium' : ''
                          }`}
                        >
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.1"
                              value={value ?? ''}
                              onChange={(e) => handleFieldEdit(idx, field.key, e.target.value)}
                              min={validationRanges[field.key]?.min}
                              max={validationRanges[field.key]?.max}
                              className="w-full bg-surface border border-border rounded px-2 py-1 text-right text-sm focus:border-accent-primary focus:outline-none"
                              placeholder="—"
                              title={validationRanges[field.key] ? `Valid range: ${validationRanges[field.key].min}-${validationRanges[field.key].max} ${validationRanges[field.key].unit}` : ''}
                            />
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              {StatusIcon && (
                                <StatusIcon className={`w-3 h-3 ${indicator.color} flex-shrink-0`} />
                              )}
                              <span className={`${indicator.color} ${!hasValue ? 'text-muted' : ''}`}>
                                {hasValue ? value.toFixed(1) : '—'}
                              </span>
                              {/* Tooltip */}
                              {indicator.tooltip && (
                                <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block z-10 w-48">
                                  <div className="bg-zinc-900 text-zinc-200 text-xs rounded px-2 py-1 border border-zinc-700 shadow-lg">
                                    {indicator.tooltip}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Missing offline distance warning */}
        {(() => {
          const shotsWithOffline = editedShots.filter(s => s.offline_distance != null).length;
          const missingPercent = ((editedShots.length - shotsWithOffline) / editedShots.length) * 100;
          return missingPercent >= 50 && editedShots.length >= 3 && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                ⚠️ <span className="font-semibold">Limited lateral/offline data detected ({shotsWithOffline}/{editedShots.length} shots).</span> If your launch monitor shows dispersion (left/right), you can edit the Offline values above. Otherwise, dispersion analysis will be limited.
              </p>
            </div>
          );
        })()}

        {/* Action buttons */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-border">
          <Button
            variant="secondary"
            onClick={onCancel}
            className="flex-1"
          >
            ✕ Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            className="flex-1"
          >
            ✓ Confirm & Analyze
          </Button>
        </div>
      </div>
    </Card>
  );
};

ExtractionPreview.propTypes = {
  extractedData: PropTypes.shape({
    shots: PropTypes.arrayOf(PropTypes.object).isRequired,
    data: PropTypes.object,
    shot_count: PropTypes.number,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
};
